import { escapeHtml } from '../ui.js';
import { roomConfig, saveRoomConfig, callFunction } from '../db.js';
import { openPanel } from './dialog.js';
import { allowedMeetingUrl } from '../room-links.js';

/**
 * TEAM → ROOM. The handful of settings behind the meeting-room display.
 *
 * Owner and Director only — the same roles that decide what the practice says
 * in its own name, because these two URLs are what a stranger in the room will
 * press a large button to join.
 *
 * The allowlist is checked HERE and again in firestore.rules. The rule is the
 * one that matters: this form only stops an honest mistake, while the rule
 * stops a hand-written document from planting a link the display would then
 * render as a Join button.
 */
const ROLES = ['Owner', 'Director'];

export async function renderRoomTab(mount, session) {
  const allowed = (session.roles || []).some((r) => ROLES.includes(r));
  if (!allowed) {
    mount.innerHTML = '<p class="muted" style="margin-top:24px">Owner or Director only.</p>';
    return;
  }

  const draw = async () => {
    const cfg = (await roomConfig('main').catch(() => null)) || {};
    const row = (label, value, hint) => `
      <div style="display:flex;gap:16px;align-items:baseline;padding:11px 0;border-top:1px solid var(--hair-soft)">
        <span style="min-width:190px;font-size:12.5px;color:var(--ink-3)">${escapeHtml(label)}</span>
        <span style="flex:1;font-size:13px">${value ? escapeHtml(String(value))
      : `<span style="color:var(--ink-3)">${escapeHtml(hint || 'Not set')}</span>`}</span>
      </div>`;

    const kiosk = `msedge --kiosk ${location.origin}/room --edge-kiosk-type=fullscreen`;
    mount.innerHTML = `
      <div style="max-width:720px">
        <div style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3)">Room display</div>
        <p class="muted" style="margin:8px 0 18px">
          The PC on the meeting room TV signs in as the room account and opens this screen full size with
          Room display at the foot of the menu. It reads today’s calendar and a wallboard: project references,
          deadlines and first names — never a fee, a rate, pay or a client’s details.
        </p>
        ${row('Room name', cfg.roomName, 'Meeting room')}
        ${row('Room calendar', cfg.calendarId, 'The room account’s address')}
        ${row('Monday meeting', cfg.mondayMeetingUrl)}
        ${row('Second office line', cfg.chennaiLineUrl)}
        ${row('Back to the wallboard after', cfg.idleTimeoutSeconds ? `${cfg.idleTimeoutSeconds} seconds` : '180 seconds')}
        ${row('Theme', cfg.theme === 'light' ? 'Light' : 'Dark')}
        ${row('Guest Wi-Fi name', cfg.guestWifiName, 'Not shown')}
        ${row('Who is coming', cfg.showAttendees === false ? 'Hidden' : 'First names of the team; guests only as a number')}
        ${row('Who’s where today', cfg.showWhereToday === false ? 'Hidden' : 'Off, on site and working from home, by first name. Never why.')}
        <div style="display:flex;gap:10px;margin-top:18px;flex-wrap:wrap">
          <button class="btn btn-primary" id="room-edit">Edit</button>
          <a class="btn" href="/room" target="_blank" rel="noopener">Preview the display</a>
          <button class="btn" id="room-rebuild" type="button">Rebuild the board now</button>
          <span id="room-rebuild-msg" class="muted" style="align-self:center;font-size:12.5px" role="status"></span>
        </div>

        <div style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3);margin-top:32px">Setting up the room PC</div>
        <ol class="muted" style="margin:10px 0 0;padding-left:20px;line-height:1.7">
          <li>On a 4K TV, Windows display scaling at 200%, so the screen is drawn at 1920 × 1080 and stays sharp.</li>
          <li>Sleep, screen saver and the TV’s own eco / auto-off all off.</li>
          <li>Sign in to Track in Edge as the room account. Room display at the foot of the menu opens this screen full size.</li>
          <li>To have it come up by itself, start Edge at sign-in with <code>${escapeHtml(kiosk)}</code>.</li>
          <li>A wireless keyboard with a touchpad, or a presenter clicker, on the table; a webcam and speakerphone if calls are joined from this PC.</li>
        </ol>
      </div>`;

    mount.querySelector('#room-rebuild').addEventListener('click', async (ev) => {
      const b = ev.currentTarget; const msg = mount.querySelector('#room-rebuild-msg');
      b.disabled = true; msg.textContent = 'Rebuilding…';
      try { await callFunction('wallboardNow', { roomId: 'main' }); msg.textContent = 'Done — the screen updates within a few seconds.'; }
      catch (e) { msg.textContent = 'Couldn’t rebuild it just now. The board rebuilds itself every five minutes.'; }
      finally { b.disabled = false; }
    });
    mount.querySelector('#room-edit').addEventListener('click', () => edit(cfg));
  };

  const edit = (cfg) => openPanel({
    title: 'Room display',
    subtitle: 'What the screen in the meeting room shows, and what it can join.',
    fields: [
      { key: 'roomName', label: 'Room name', type: 'text', span: true,
        value: cfg.roomName || 'Meeting room' },
      { key: 'calendarId', label: 'Room calendar', type: 'text', span: true,
        value: cfg.calendarId || '', placeholder: 'meetingroom@yourcompany.com',
        help: 'The room account’s own address. Only this calendar is ever read.' },
      { key: 'mondayMeetingUrl', label: 'Monday meeting', type: 'text', span: true, optional: true,
        value: cfg.mondayMeetingUrl || '',
        help: 'Meet, Teams or Zoom only — anything else is refused on save.' },
      { key: 'chennaiLineUrl', label: 'Second office line', type: 'text', span: true, optional: true,
        value: cfg.chennaiLineUrl || '', help: 'The open video line to your other office (key C on the screen).' },
      { key: 'idleTimeoutSeconds', label: 'Back to the wallboard after', type: 'number',
        value: String(cfg.idleTimeoutSeconds || 180), help: 'Seconds. 30 at the least.' },
      { key: 'theme', label: 'Theme', type: 'select', value: cfg.theme || 'dark',
        options: [{ value: 'dark', label: 'Dark' }, { value: 'light', label: 'Light' }] },
      { key: 'guestWifiName', label: 'Guest Wi-Fi name', type: 'text', span: true, optional: true,
        value: cfg.guestWifiName || '', placeholder: 'Guest',
        help: 'Shown on the visitor screen. The password is never stored — keep it on a card on the table.' },
      { key: 'showAttendees', label: 'Show who is coming', type: 'checkbox', span: true,
        value: cfg.showAttendees !== false,
        sublabel: 'First names of the team and their reply. People from outside only as a number, never a name or address.' },
      { key: 'showWhereToday', label: 'Show who’s where today', type: 'checkbox', span: true,
        value: cfg.showWhereToday !== false,
        sublabel: 'Who is off, on site or working from home, by first name. Time off always says just “Off”.' },
    ],
    primary: {
      label: 'Save',
      onSubmit: async (v) => {
        const monday = String(v.mondayMeetingUrl || '').trim();
        const chennai = String(v.chennaiLineUrl || '').trim();
        // Named plainly rather than "invalid URL": the reason it is refused is
        // the point, and the rule will refuse it again regardless.
        if (monday && !allowedMeetingUrl(monday)) {
          throw new Error('The Monday link must be an https Meet, Teams or Zoom address.');
        }
        if (chennai && !allowedMeetingUrl(chennai)) {
          throw new Error('The second office link must be an https Meet, Teams or Zoom address.');
        }
        const secs = Math.max(30, Math.min(3600, Number(v.idleTimeoutSeconds) || 180));
        await saveRoomConfig('main', {
          roomName: String(v.roomName || 'Meeting room').slice(0, 60),
          calendarId: String(v.calendarId || '').trim().slice(0, 140),
          mondayMeetingUrl: monday,
          chennaiLineUrl: chennai,
          idleTimeoutSeconds: secs,
          theme: v.theme === 'light' ? 'light' : 'dark',
          guestWifiName: String(v.guestWifiName || '').replace(/[<>]/g, '').trim().slice(0, 40),
          showAttendees: v.showAttendees !== false,
          showWhereToday: v.showWhereToday !== false,
          updatedBy: session.email,
          updatedAt: new Date().toISOString(),
        });
        draw();
      },
    },
  });

  draw();
}
